import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api/client';
import './AiIntakeChat.css';

const CRISIS_MESSAGE = `If you are in immediate danger or thinking about harming yourself, please contact your local emergency services right now, or reach a crisis line in your area. A member of our clinical team has also been alerted and will reach out as soon as possible.`;

export default function AiIntakeChat() {
  const { appointmentId } = useParams();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi, I'm the AI intake assistant for Global Mind Wellness Centre — not a clinician. I'll ask a few questions to help your clinician prepare for your appointment. Everything you share will be reviewed by a licensed professional before your session. Let's start: what brings you in today?",
    },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [riskFlag, setRiskFlag] = useState('none');
  const [finalized, setFinalized] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function sendMessage(e) {
    e.preventDefault();
    if (!input.trim()) return;
    setError('');
    const userMessage = input;
    setMessages((m) => [...m, { role: 'user', content: userMessage }]);
    setInput('');
    setBusy(true);
    try {
      const res = await apiClient.post(`/ai-intake/${appointmentId}/message`, { message: userMessage });
      setMessages((m) => [...m, { role: 'assistant', content: res.data.reply }]);
      if (res.data.riskFlag && res.data.riskFlag !== 'none') {
        setRiskFlag(res.data.riskFlag);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function handleFinalize() {
    setBusy(true);
    try {
      await apiClient.post(`/ai-intake/${appointmentId}/finalize`);
      setFinalized(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not complete intake.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="intake-page">
      <div className="container intake-inner">
        <div className="intake-disclaimer">
          <strong>This is an AI assistant, not a clinician.</strong> It only gathers information
          to prepare your care team. It cannot diagnose or prescribe.
        </div>

        {riskFlag === 'crisis' && (
          <div className="alert alert-error intake-crisis">{CRISIS_MESSAGE}</div>
        )}

        <div className="card intake-window">
          <div className="intake-messages">
            {messages.map((m, i) => (
              <div key={i} className={`intake-bubble ${m.role}`}>
                <span className="intake-bubble-label">{m.role === 'assistant' ? 'AI Assistant' : 'You'}</span>
                <div className="intake-bubble-body"><ReactMarkdown>{m.content}</ReactMarkdown></div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          {!finalized ? (
            <form className="intake-input-row" onSubmit={sendMessage}>
              <input
                placeholder="Type your response…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={busy}
              />
              <button className="btn btn-primary" disabled={busy}>Send</button>
            </form>
          ) : (
            <div className="alert alert-success">
              Intake complete. Your clinician will review this before your appointment.
            </div>
          )}

          {!finalized && (
            <button className="btn btn-outline intake-finish-btn" onClick={handleFinalize} disabled={busy}>
              I'm done — finish intake
            </button>
          )}
        </div>

        <Link to="/dashboard" className="intake-back-link">Back to dashboard</Link>
      </div>
    </div>
  );
}
